import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

export interface State {
  optionLocationData: { value: Location } | null;
  optionModeData: { value: Mode | null } | null;
  isLoading: boolean;
  error: unknown;
}

type Location = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

type Mode = 'manual' | 'auto';

export type LocationSettingsStoreState = State;

class LocationSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('location-settings');
  }

  override createInitialState() {
    return {
      optionLocationData: null,
      optionModeData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const optionLocationPromise = api.geolocation.getOptionLocation();
      const optionModePromise = api.geolocation.getOptionMode?.().catch((error: unknown) => {
        // Only exists on <=2019 models.
        if (typeof error === 'object' && error !== null && 'code' in error && error.code === 404) {
          return { value: null };
        }

        throw error;
      });

      const [optionLocationData, optionModeData] = await Promise.all([
        optionLocationPromise,
        optionModePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLocationData: optionLocationData as unknown as { value: Location },
        optionModeData: optionModeData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { locationSettings: this.store(selectAll, shallow) };
  }

  async setOptionLocation({ value }: { value: Location }) {
    const api = this.getApi();
    const result = (await api.geolocation.setOptionLocation({ value })) as unknown as {
      value: Location;
    };
    this.set({ optionLocationData: result });
  }

  async setOptionMode({ value }: { value: Mode }) {
    const api = this.getApi();
    const result = await api.geolocation.setOptionMode({ value });
    this.set({ optionModeData: result });
  }
}

function selectAll(state: LocationSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLocationData: state.optionLocationData,
    optionModeData: state.optionModeData,
  };
}

export const locationSettingsStore = new LocationSettingsStore();
